/**
 * MP'S SMALL LISTS, AS WOODSIDE ACTUALLY HOLDS THEM
 * =================================================
 *
 * Read from production on 2026-09-17 so the add-person sheet offers the same
 * words staff see in MP rather than a plausible-looking invention.
 *
 * THESE BELONG IN THE API PAYLOAD, not here. They are ids, and an id typed
 * into a widget is a thing that rots the first time somebody adds a shirt
 * size. They sit here only while the sheet is being designed against the
 * mock; the read path should serve them beside the household, the way the
 * profile widget already receives its `lookups`.
 */

export interface Lookup {
  id: number;
  label: string;
}

export const GENDERS: Lookup[] = [
  { id: 1, label: 'Male' },
  { id: 2, label: 'Female' },
];

export const MARITAL_STATUSES: Lookup[] = [
  { id: 1, label: 'Single' },
  { id: 2, label: 'Married' },
  { id: 5, label: 'Partnered' },
  { id: 3, label: 'Divorced' },
  { id: 6, label: 'Separated' },
  { id: 4, label: 'Widowed' },
];

/**
 * MP's own list carries a shouted warning against adding to it, and two of the
 * six are not a congregant's business: Company is for organisations, and Guest
 * Child is a check-in arrangement staff make. The three left are the ones a
 * person can honestly answer about their own household.
 */
export const HOUSEHOLD_POSITIONS: Lookup[] = [
  { id: 1, label: 'Head of household' },
  { id: 2, label: 'Minor child' },
  { id: 4, label: 'Adult child' },
  { id: 3, label: 'Other adult' },
  { id: 5, label: 'Guest child' },
];

export interface ShirtSize extends Lookup {
  group: string;
  sort: number;
}

export const SHIRT_SIZES: ShirtSize[] = [
  { id: 1, label: '2T', group: 'Toddler', sort: 10 },
  { id: 3, label: '3T', group: 'Toddler', sort: 20 },
  { id: 5, label: '4T', group: 'Toddler', sort: 30 },
  { id: 7, label: '5/6T', group: 'Toddler', sort: 40 },
  { id: 17, label: 'Youth XS', group: 'Youth', sort: 100 },
  { id: 15, label: 'Youth S', group: 'Youth', sort: 110 },
  { id: 14, label: 'Youth M', group: 'Youth', sort: 120 },
  { id: 13, label: 'Youth L', group: 'Youth', sort: 130 },
  { id: 16, label: 'Youth XL', group: 'Youth', sort: 140 },
  { id: 12, label: 'XS', group: 'Adult', sort: 200 },
  { id: 10, label: 'S', group: 'Adult', sort: 210 },
  { id: 9, label: 'M', group: 'Adult', sort: 220 },
  { id: 8, label: 'L', group: 'Adult', sort: 230 },
  { id: 11, label: 'XL', group: 'Adult', sort: 240 },
  { id: 2, label: '2XL', group: 'Adult', sort: 250 },
  { id: 4, label: '3XL', group: 'Adult', sort: 260 },
  { id: 6, label: '4XL', group: 'Adult', sort: 270 },
];

export const SHIRT_GROUPS = ['Toddler', 'Youth', 'Adult'];

/** Whole years, or null when the birthday is missing or not a date. */
export function ageFrom(dateOfBirth: string): number | null {
  const born = new Date(dateOfBirth);
  if (Number.isNaN(born.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - born.getFullYear();
  const beforeBirthday =
    now.getMonth() < born.getMonth() ||
    (now.getMonth() === born.getMonth() && now.getDate() < born.getDate());
  if (beforeBirthday) age -= 1;
  return age >= 0 && age < 130 ? age : null;
}

/**
 * Where somebody sits in a household, worked out rather than asked.
 *
 * "Household position" is staff vocabulary, and a parent adding their
 * eight-year-old should not have to learn it to finish a form. The
 * relationship and the birthday already say it: a spouse or a parent heads a
 * household, a child is minor or adult depending on the day they were born.
 * The sheet still shows the answer, because a guess a person cannot see is a
 * guess nobody can correct.
 *
 * `relationshipId` is keyed the way the widget keys everything — it says what
 * the OTHER person is to the viewer. See lib/relationships.ts.
 */
export function positionFor(relationshipId: number | null, dateOfBirth: string): number | null {
  const age = dateOfBirth ? ageFrom(dateOfBirth) : null;
  // A spouse heads the household alongside you.
  if (relationshipId === 1) return 1;
  // Your child or stepchild: minor until they are not.
  if (relationshipId === 6 || relationshipId === 41) return age == null ? null : age < 18 ? 2 : 4;
  if (age == null) return null;
  // Family — a grandchild, a niece, a parent who has moved in — is a minor
  // child or another adult. A minor who is not family (a friend, a neighbor's
  // kid, no answer) is a Guest Child: they are staying with you, not yours.
  // The sheet shows the answer so anybody who disagrees with it can say so,
  // and the server holds the guest rule either way.
  if (age < 18) return relationshipId === 12 || relationshipId === 3 ? 2 : 5;
  return 3;
}
